import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from '../../../app/LanguageContext';
import {
  formatCurrency,
  formatNumberAsCurrencyMask,
  formatPercentInputFromNumber,
  handleCurrencyInputChange,
  handlePercentInputChange,
} from '../../../app/currency';
import { ProductSkuPicker, skuVariationLabel } from '../../../modules/products/public';
import { addConsignmentItem, updateConsignmentItem } from './consignmentApi';
import {
  buildItemTermsPayload,
  buildStoreShareFromPercent,
  buildStoreSharePercentFromAmount,
  computeConsignmentFinancials,
  getStoreSharePercentFromItem,
  isFixedNetCompanyMode,
  normalizeNegotiationMode,
  parseCurrencyNumber,
  parsePercentNumber,
} from './consignmentFinancials';

const NEGOTIATION_MODES = [
  { value: 'FIXED_PRICE', labelKey: 'consignment.mode_agreed_sale_price' },
  { value: 'FREE_PRICE', labelKey: 'consignment.mode_fixed_net_company' },
];

const emptyPickerForm = () => ({
  negotiation_mode: 'FIXED_PRICE',
  product: null,
  sku: null,
});

function buildSkuSnapshot(sku) {
  if (!sku) {
    return { color: '', size: '' };
  }
  const attrs = sku.attributes || {};
  return {
    color: attrs.color || attrs.Cor || '',
    size: attrs.size || attrs.Tamanho || '',
  };
}

export default function ConsignmentItemForm({
  consignmentId,
  status,
  editingItem,
  onSaved,
  onCancelEdit,
  onError,
}) {
  const { translate } = useTranslation();
  const isActive = status === 'ACTIVE';
  const [form, setForm] = useState(emptyPickerForm());
  const [pickerKey, setPickerKey] = useState(0);
  const [salePriceDisplay, setSalePriceDisplay] = useState('');
  const [storeShareDisplay, setStoreShareDisplay] = useState('');
  const [storeSharePercentDisplay, setStoreSharePercentDisplay] = useState('');
  const [fixedNetDisplay, setFixedNetDisplay] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [observation, setObservation] = useState('');
  const [saving, setSaving] = useState(false);

  const isFixedNetMode = isFixedNetCompanyMode(form.negotiation_mode);
  const saleAmount = parseCurrencyNumber(salePriceDisplay);
  const storeShareAmount = parseCurrencyNumber(storeShareDisplay);
  const storeSharePercent = parsePercentNumber(storeSharePercentDisplay);
  const fixedNetAmount = parseCurrencyNumber(fixedNetDisplay);
  const quantityValue = Math.max(0, parseInt(quantity, 10) || 0);

  const financials = useMemo(() => {
    if (isFixedNetMode) {
      return { sale: 0, storeShare: 0, net: fixedNetAmount };
    }
    return computeConsignmentFinancials({
      sale_price: saleAmount,
      negotiation_mode: form.negotiation_mode,
      store_share_amount: storeShareAmount,
    });
  }, [isFixedNetMode, fixedNetAmount, saleAmount, storeShareAmount, form.negotiation_mode]);

  const syncStoreShareFromPercent = useCallback((percent, sale) => {
    const amount = buildStoreShareFromPercent(sale, percent);
    setStoreShareDisplay(amount > 0 ? formatNumberAsCurrencyMask(amount) : '');
  }, []);

  const syncPercentFromStoreShare = useCallback((amount, sale) => {
    const percent = buildStoreSharePercentFromAmount(sale, amount);
    setStoreSharePercentDisplay(percent > 0 ? formatPercentInputFromNumber(percent) : '');
  }, []);

  const applyCurrencyChange = useCallback((synth, onAmount) => {
    if (typeof synth === 'string') {
      onAmount('', 0);
      return;
    }
    const display = synth.target?.value ?? '';
    const amount = synth.target?.numericValue ?? 0;
    onAmount(display, amount);
  }, []);

  const loadMoneyDisplaysFromItem = useCallback((item) => {
    const mode = normalizeNegotiationMode(item.negotiation_mode);
    if (mode === 'FREE_PRICE') {
      setFixedNetDisplay(item.net_value ? formatNumberAsCurrencyMask(item.net_value) : '');
      setSalePriceDisplay('');
      setStoreShareDisplay('');
      setStoreSharePercentDisplay('');
    } else {
      const rowFinancials = computeConsignmentFinancials(item);
      const storePct = getStoreSharePercentFromItem(item);
      setSalePriceDisplay(item.sale_price ? formatNumberAsCurrencyMask(item.sale_price) : '');
      setStoreShareDisplay(rowFinancials.storeShare ? formatNumberAsCurrencyMask(rowFinancials.storeShare) : '');
      setStoreSharePercentDisplay(storePct ? formatPercentInputFromNumber(storePct) : '');
      setFixedNetDisplay('');
    }
    setQuantity(String(item.qty_sent > 0 ? item.qty_sent : 1));
  }, []);

  useEffect(() => {
    if (editingItem) {
      setForm({
        negotiation_mode: normalizeNegotiationMode(editingItem.negotiation_mode),
        product: null,
        sku: null,
      });
      loadMoneyDisplaysFromItem(editingItem);
      setObservation('');
    } else {
      setForm(emptyPickerForm());
      setSalePriceDisplay('');
      setStoreShareDisplay('');
      setStoreSharePercentDisplay('');
      setFixedNetDisplay('');
      setQuantity('1');
      setObservation('');
      setPickerKey((k) => k + 1);
    }
  }, [editingItem, loadMoneyDisplaysFromItem]);

  const handleModeChange = (event) => {
    const nextMode = event.target.value;
    setForm((f) => ({ ...f, negotiation_mode: nextMode }));
    if (nextMode === 'FREE_PRICE') {
      setSalePriceDisplay('');
      setStoreShareDisplay('');
      setStoreSharePercentDisplay('');
    } else {
      setFixedNetDisplay('');
    }
  };

  const handleSalePriceChange = (event) => {
    handleCurrencyInputChange(event, (synth) => {
      applyCurrencyChange(synth, (display, sale) => {
        setSalePriceDisplay(display);
        if (storeSharePercentDisplay.trim()) {
          syncStoreShareFromPercent(parsePercentNumber(storeSharePercentDisplay), sale);
        } else if (storeShareDisplay.trim()) {
          syncPercentFromStoreShare(parseCurrencyNumber(storeShareDisplay), sale);
        }
      });
    });
  };

  const handleStoreShareAmountChange = (event) => {
    handleCurrencyInputChange(event, (synth) => {
      applyCurrencyChange(synth, (display, amount) => {
        setStoreShareDisplay(display);
        if (!display.trim()) {
          setStoreSharePercentDisplay('');
          return;
        }
        syncPercentFromStoreShare(amount, saleAmount);
      });
    });
  };

  const handleStoreSharePercentChange = (event) => {
    handlePercentInputChange(event, (synth) => {
      const display = synth.target.value;
      setStoreSharePercentDisplay(display);
      if (!display.trim()) {
        setStoreShareDisplay('');
        return;
      }
      const percent = synth.numericValue ?? parsePercentNumber(display);
      syncStoreShareFromPercent(percent, saleAmount);
    });
  };

  const handleFixedNetChange = (event) => {
    handleCurrencyInputChange(event, (synth) => {
      applyCurrencyChange(synth, (display) => {
        setFixedNetDisplay(display);
      });
    });
  };

  const resetFormForNewItem = () => {
    setForm((f) => ({ ...f, product: null, sku: null }));
    setPickerKey((k) => k + 1);
    setObservation('');
  };

  const validateForm = () => {
    if (!editingItem) {
      if (!form.sku?.id) return translate('consignment.item_sku_required');
      if (!form.product?.name && !form.product?.code) return translate('consignment.item_product_required');
    }
    if (isFixedNetMode) {
      if (fixedNetAmount <= 0) return translate('consignment.fixed_net_required');
    } else if (saleAmount <= 0) {
      return translate('consignment.item_sale_price_required');
    }
    if (quantityValue < 1) return translate('consignment.item_quantity_required');
    if (isActive && !observation.trim()) return translate('consignment.observation_required');
    return '';
  };

  const buildAddPayload = (terms) => {
    const sku = form.sku;
    const product = form.product;
    const skuSnap = buildSkuSnapshot(sku);
    return {
      original_product_id: product?.id || '',
      original_sku_id: sku?.id || '',
      sku_code: sku?.code || '',
      product_name: product?.name || product?.code || '',
      product_sku_display: skuVariationLabel(sku) || sku?.code || '',
      category: product?.category || '',
      brand: product?.brand || '',
      color: skuSnap.color,
      size: skuSnap.size,
      description: product?.description || '',
      cost: sku?.cost || 0,
      suggested_price: sku?.price || 0,
      qty_sent: quantityValue,
      observation: observation.trim(),
      ...terms,
    };
  };

  const persistItem = async () => {
    const validationError = validateForm();
    if (validationError) {
      onError(validationError);
      return;
    }
    setSaving(true);
    try {
      const terms = buildItemTermsPayload(
        form.negotiation_mode,
        saleAmount,
        storeShareAmount,
        storeSharePercent,
        fixedNetAmount
      );
      terms.qty_sent = quantityValue;
      if (isActive) {
        terms.observation = observation.trim();
      }

      if (editingItem) {
        await updateConsignmentItem(consignmentId, editingItem.id, terms);
        onCancelEdit();
        onSaved(translate('consignment.item_saved'));
      } else {
        await addConsignmentItem(consignmentId, buildAddPayload(terms));
        resetFormForNewItem();
        onSaved(translate('consignment.item_saved'));
      }
    } catch (e) {
      onError(e.message || translate('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const suggestedPrice = form.sku?.price;
  const canEditQuantity = !editingItem || (editingItem.qty_sold === 0 && editingItem.qty_returned === 0);

  if (!consignmentId) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        {editingItem ? translate('consignment.edit_item') : translate('consignment.add_item')}
      </Typography>
      <Box display="flex" flexDirection="column" gap={2}>
        {!editingItem && (
          <ProductSkuPicker
            key={pickerKey}
            skuId={null}
            onChange={({ product, sku }) => {
              setForm((f) => ({ ...f, product, sku }));
              if (!isFixedNetMode && sku?.price > 0 && !salePriceDisplay.trim()) {
                setSalePriceDisplay(formatNumberAsCurrencyMask(sku.price));
              }
            }}
          />
        )}

        {canEditQuantity && (
          <TextField
            label={translate('consignment.item_quantity')}
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value.replace(/\D/g, '').slice(0, 6))}
            fullWidth
            size="small"
            inputProps={{ min: 1, step: 1 }}
            helperText={translate('consignment.item_quantity_help')}
          />
        )}

        <Typography variant="subtitle2" color="text.secondary">
          {translate('consignment.section_commercial_terms')}
        </Typography>
        <FormHelperText sx={{ mt: -1.5 }}>
          {translate('consignment.commercial_terms_unit_note')}
        </FormHelperText>

        <FormControl component="fieldset" variant="standard">
          <RadioGroup value={form.negotiation_mode} onChange={handleModeChange}>
            {NEGOTIATION_MODES.map((m) => (
              <FormControlLabel
                key={m.value}
                value={m.value}
                control={<Radio size="small" />}
                label={(
                  <Box>
                    <Typography variant="body2">{translate(m.labelKey)}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {translate(`${m.labelKey}_help`)}
                    </Typography>
                  </Box>
                )}
                sx={{ alignItems: 'flex-start', mb: 1 }}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {!isFixedNetMode ? (
          <>
            <TextField
              label={translate('consignment.agreed_sale_price_label')}
              value={salePriceDisplay}
              onChange={handleSalePriceChange}
              fullWidth
              size="small"
              inputProps={{ inputMode: 'decimal' }}
            />
            <FormHelperText sx={{ mt: -1.5 }}>
              {translate('consignment.agreed_sale_price_help')}
              {suggestedPrice != null && suggestedPrice > 0 && (
                <> · {translate('consignment.suggested_price_hint', { price: formatCurrency(suggestedPrice) })}</>
              )}
            </FormHelperText>

            <Box display="flex" gap={2}>
              <TextField
                label={translate('consignment.store_share_amount_label')}
                value={storeShareDisplay}
                onChange={handleStoreShareAmountChange}
                fullWidth
                size="small"
                inputProps={{ inputMode: 'decimal' }}
              />
              <TextField
                label={translate('consignment.store_share_percent_label')}
                value={storeSharePercentDisplay}
                onChange={handleStoreSharePercentChange}
                fullWidth
                size="small"
                inputProps={{ inputMode: 'decimal' }}
              />
            </Box>
            <FormHelperText sx={{ mt: -1.5 }}>
              {translate('consignment.store_share_linked_help')}
            </FormHelperText>
          </>
        ) : (
          <>
            <TextField
              label={translate('consignment.fixed_net_company_label')}
              value={fixedNetDisplay}
              onChange={handleFixedNetChange}
              fullWidth
              size="small"
              inputProps={{ inputMode: 'decimal' }}
            />
            <FormHelperText sx={{ mt: -1.5 }}>
              {translate('consignment.fixed_net_company_help')}
            </FormHelperText>
          </>
        )}

        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
          <Typography variant="caption" color="text.secondary">{translate('consignment.financial_summary')}</Typography>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2">{translate('consignment.net_value_company')}</Typography>
            <Typography variant="body2" fontWeight="bold" color="primary">
              {formatCurrency(financials.net)}
            </Typography>
          </Box>
        </Paper>

        {isActive && (
          <TextField
            label={translate('consignment.change_observation')}
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
            fullWidth
            multiline
            rows={2}
            size="small"
            required
          />
        )}

        <Box display="flex" flexWrap="wrap" gap={1}>
          {editingItem && (
            <Button size="small" onClick={onCancelEdit} disabled={saving}>
              {translate('common.cancel')}
            </Button>
          )}
          <Button size="small" variant="contained" onClick={() => persistItem()} disabled={saving}>
            {saving ? <CircularProgress size={16} /> : translate('common.save')}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}
